import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";

import { cn } from "@/lib/utils";
import { apiClient } from "@/lib/api";
import type { PersonPool, ScopeKind } from "@tilt/admin-sdk";

/**
 * Pool-aware, scope-filtered role picker.
 *
 * Fetches roles from GET /admin/v1/roles so custom roles created by
 * partner_admins appear alongside system roles. The hardcoded pool split
 * distinguishes admin-only slugs (super_admin, partner_viewer,
 * location_manager) from POS-only slugs (partner_reporter, org_reporter,
 * location_supervisor, location_cashier, kiosk_device). Roles that share a
 * slug across pools (partner_admin, organization_admin) show in both.
 * Custom roles (is_system=false) are currently admin-pool only.
 *
 * Scope filtering: system roles use SYSTEM_SLUG_SCOPES; custom roles are
 * shown at every scope (the assignment UI controls the scope value).
 */

export interface RoleSelectProps {
  pool: PersonPool;
  scopeKind: ScopeKind | null;
  value: string;
  onChange: (slug: string) => void;
  className?: string;
  id?: string;
  /** Suppress super_admin from the option list - set when the caller
   *  is not a super_admin (the API will reject anyway, but hiding it
   *  matches the "don't show options the user can't use" rule). */
  hideSuperAdmin?: boolean;
}

// System role slugs that only exist in the admin pool.
const ADMIN_ONLY_SLUGS = new Set(["super_admin", "partner_viewer", "location_manager"]);
// System role slugs that only exist in the POS pool.
const POS_ONLY_SLUGS = new Set([
  "partner_reporter",
  "org_reporter",
  "location_supervisor",
  "location_cashier",
  "kiosk_device",
]);

// Maps system slugs → the scope kinds at which they may be granted.
// role_tbl has no valid_scopes column; this mirrors the backend matrix.
// Custom roles (is_system=false) are not listed - they show at any scope.
const SYSTEM_SLUG_SCOPES: Readonly<Record<string, readonly string[]>> = {
  super_admin: ["partner"],
  partner_admin: ["partner"],
  partner_viewer: ["partner"],
  partner_reporter: ["partner"],
  organization_admin: ["organization"],
  org_reporter: ["organization"],
  location_manager: ["location"],
  location_supervisor: ["location"],
  location_cashier: ["location"],
  kiosk_device: ["location"],
};

export function RoleSelect(props: RoleSelectProps) {
  const rolesQuery = useQuery({
    queryKey: ["roles", "list"],
    queryFn: () => apiClient.listRoles({ include_system: true }),
    staleTime: 60_000,
  });

  const options = useMemo(() => {
    if (!rolesQuery.data) return [];
    return rolesQuery.data.roles.filter((r) => {
      if (r.is_deleted) return false;
      if (props.hideSuperAdmin && r.slug === "super_admin") return false;
      // Pool filter: custom roles are admin-pool only for now.
      if (props.pool === "admin") {
        if (r.is_system && POS_ONLY_SLUGS.has(r.slug)) return false;
      } else {
        // pos pool: exclude admin-only system slugs + all custom roles
        if (r.is_system ? ADMIN_ONLY_SLUGS.has(r.slug) : true) return false;
      }
      // Scope filter: system roles use the slug→scopes table; custom roles
      // show at every scope (the caller controls the scope assignment).
      if (props.scopeKind && r.is_system) {
        const validScopes = SYSTEM_SLUG_SCOPES[r.slug];
        if (validScopes && !validScopes.includes(props.scopeKind)) return false;
      }
      return true;
    });
  }, [rolesQuery.data, props.pool, props.scopeKind, props.hideSuperAdmin]);

  if (rolesQuery.isLoading) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading roles…
      </p>
    );
  }

  const current = options.find((o) => o.slug === props.value);

  return (
    <div className="space-y-1">
      <select
        id={props.id}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className={cn(
          "w-full rounded-md border border-slate-300 px-3 py-2 text-sm",
          props.className,
        )}
      >
        <option value="">
          {props.scopeKind
            ? options.length === 0
              ? `No roles available for ${props.scopeKind} scope`
              : "Select a role…"
            : "Pick a workplace first"}
        </option>
        {options.map((o) => (
          <option key={o.id} value={o.slug}>
            {o.name}
            {!o.is_system && o.partner_name ? ` · ${o.partner_name}` : ""}
          </option>
        ))}
      </select>
      {current?.description && <p className="text-xs text-slate-500">{current.description}</p>}
    </div>
  );
}
